import React, { useContext } from "react";
import { Navigate } from "react-router-dom";
import { Alert, Card, Spinner } from "react-bootstrap";
import {
  IconShieldOff,
  IconUsers,
  IconLock,
  IconClock,
} from "@tabler/icons-react";
import UserContext from "../../../contexts/UserContext";

const ProtectedRoute = ({
  children,
  requiredPermission = null,
  requiredModule = null,
  requiredAction = "read",
  requireSuperuser = false,
  requiredLevel = null,
  fallbackPath = "/login",
  showLoadingForPermissions = false,
}) => {
  const {
    user,
    logged,
    hasPermission,
    permissionsLoading,
    permissionsError,
    isInitializing, // NOVO: Estado de inicialização do contexto
  } = useContext(UserContext);

  // Debug logs aprimorados
  console.log("🛡️ ProtectedRoute - Estado atual:", {
    isInitializing,
    logged,
    user: user
      ? {
          id: user.user_id,
          name: user.first_name,
          level: user.superuser_level,
          is_superuser: user.is_superuser,
        }
      : null,
    requiredModule,
    requiredAction,
    requireSuperuser,
    requiredLevel,
    permissionsLoading,
    hasPermissionResult:
      requiredModule && user
        ? hasPermission(requiredModule, requiredAction)
        : "N/A",
  });

  // ===============================================
  // 1. VERIFICAÇÃO DE INICIALIZAÇÃO (NOVA)
  // ===============================================
  if (isInitializing) {
    console.log(
      "⏳ ProtectedRoute: Aguardando inicialização do contexto do usuário"
    );
    return (
      <div className="container mt-5">
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center p-5">
            <IconClock size={64} className="text-primary mb-3" />
            <h5 className="text-primary mb-2">Inicializando Sistema</h5>
            <p className="text-muted mb-3">
              Verificando autenticação e carregando configurações...
            </p>
            <Spinner animation="border" variant="primary" size="sm" />
          </Card.Body>
        </Card>
      </div>
    );
  }

  // ===============================================
  // 2. VERIFICAÇÃO DE AUTENTICAÇÃO
  // ===============================================
  if (!logged || !user) {
    console.log(
      "❌ ProtectedRoute: Usuário não logado, redirecionando para login"
    );
    return <Navigate to={fallbackPath} replace />;
  }

  // ===============================================
  // 3. VERIFICAÇÃO DE LOADING DE PERMISSÕES (OPCIONAL)
  // ===============================================
  if (showLoadingForPermissions && permissionsLoading && requiredModule) {
    console.log(
      "⏳ ProtectedRoute: Carregando permissões específicas do módulo"
    );
    return (
      <div className="container mt-5">
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center p-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <h5 className="text-primary mb-2">Verificando Permissões</h5>
            <p className="text-muted mb-1">
              Carregando suas permissões para o módulo{" "}
              <strong>{requiredModule}</strong>
            </p>
            <small className="text-muted">
              Isso pode levar alguns segundos...
            </small>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // ===============================================
  // 4. TRATAMENTO DE ERRO NAS PERMISSÕES
  // ===============================================
  if (permissionsError && requiredModule && showLoadingForPermissions) {
    console.log(
      "❌ ProtectedRoute: Erro ao carregar permissões, usando fallback"
    );
    return (
      <div className="container mt-5">
        <Card className="border-warning">
          <Card.Body className="text-center p-5">
            <IconShieldOff size={64} className="text-warning mb-3" />
            <h4 className="text-warning mb-3">Erro ao Verificar Permissões</h4>
            <p className="text-muted mb-4">
              Não foi possível carregar suas permissões detalhadas. O sistema
              está usando permissões básicas baseadas no seu nível de usuário.
            </p>
            <Alert variant="warning" className="mb-3">
              <div className="d-flex align-items-center">
                <IconShieldOff size={20} className="me-2" />
                <div>
                  <strong>Erro:</strong>{" "}
                  {permissionsError?.message || "Erro desconhecido"}
                </div>
              </div>
            </Alert>
            <small className="text-muted">
              Se o problema persistir, entre em contato com o administrador.
            </small>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // ===============================================
  // 5. VERIFICAÇÃO DE SUPERUSUÁRIO
  // ===============================================
  if (requireSuperuser && !user.is_superuser) {
    console.log(
      "❌ ProtectedRoute: Requer superusuário mas usuário não possui privilégios"
    );
    return (
      <div className="container mt-5">
        <Card className="border-danger">
          <Card.Body className="text-center p-5">
            <IconShieldOff size={64} className="text-danger mb-3" />
            <h4 className="text-danger mb-3">Acesso Restrito</h4>
            <p className="text-muted mb-4">
              Esta área requer privilégios de <strong>administrador</strong>.
              Somente usuários com permissões especiais podem acessar este
              conteúdo.
            </p>
            <Alert variant="danger">
              <div className="d-flex justify-content-between align-items-center">
                <span>
                  <strong>Seu status atual:</strong>
                </span>
                <span className="badge bg-secondary">
                  {user.is_superuser ? "Superusuário" : "Usuário Comum"}
                </span>
              </div>
            </Alert>
            <small className="text-muted">
              Entre em contato com seu administrador para solicitar acesso.
            </small>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // ===============================================
  // 6. VERIFICAÇÃO DE NÍVEL HIERÁRQUICO
  // ===============================================
  if (requiredLevel !== null && user.superuser_level > requiredLevel) {
    console.log("❌ ProtectedRoute: Nível hierárquico insuficiente", {
      userLevel: user.superuser_level,
      requiredLevel,
    });

    const levelNames = {
      1: "Admin",
      2: "Gerente",
      3: "Supervisor",
      4: "Atendente",
    };

    const levelColors = {
      1: "danger",
      2: "warning",
      3: "info",
      4: "secondary",
    };

    return (
      <div className="container mt-5">
        <Card className="border-warning">
          <Card.Body className="text-center p-5">
            <IconLock size={64} className="text-warning mb-3" />
            <h4 className="text-warning mb-3">Nível de Acesso Insuficiente</h4>
            <p className="text-muted mb-4">
              Esta funcionalidade requer nível{" "}
              <strong>{levelNames[requiredLevel]}</strong> ou superior.
            </p>
            <div className="row justify-content-center">
              <div className="col-md-8">
                <Alert variant="warning">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>
                      <strong>Nível necessário:</strong>
                    </span>
                    <span className={`badge bg-${levelColors[requiredLevel]}`}>
                      {levelNames[requiredLevel]} (Nível {requiredLevel})
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span>
                      <strong>Seu nível atual:</strong>
                    </span>
                    <span
                      className={`badge bg-${
                        levelColors[user.superuser_level] || "secondary"
                      }`}
                    >
                      {levelNames[user.superuser_level] || "Não definido"}{" "}
                      (Nível {user.superuser_level})
                    </span>
                  </div>
                </Alert>
              </div>
            </div>
            <small className="text-muted">
              Solicite uma promoção ao seu supervisor ou administrador.
            </small>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // ===============================================
  // 7. VERIFICAÇÃO DE PERMISSÃO POR MÓDULO
  // ===============================================
  if (requiredModule && !hasPermission(requiredModule, requiredAction)) {
    console.log("❌ ProtectedRoute: Sem permissão para módulo", {
      module: requiredModule,
      action: requiredAction,
    });

    const actionLabels = {
      create: "criar",
      read: "visualizar",
      update: "editar",
      delete: "excluir",
    };

    const actionIcons = {
      create: "➕",
      read: "👁️",
      update: "✏️",
      delete: "🗑️",
    };

    return (
      <div className="container mt-5">
        <Card className="border-info">
          <Card.Body className="text-center p-5">
            <IconUsers size={64} className="text-info mb-3" />
            <h4 className="text-info mb-3">Permissão Negada</h4>
            <p className="text-muted mb-4">
              Você não tem permissão para{" "}
              <strong>
                {actionIcons[requiredAction]} {actionLabels[requiredAction]}
              </strong>{" "}
              no módulo <strong>{requiredModule}</strong>.
            </p>

            <div className="row justify-content-center mb-4">
              <div className="col-md-8">
                <Alert variant="info">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>
                      <strong>Módulo:</strong>
                    </span>
                    <span className="badge bg-primary">{requiredModule}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>
                      <strong>Ação necessária:</strong>
                    </span>
                    <span className="badge bg-info">
                      {actionIcons[requiredAction]}{" "}
                      {actionLabels[requiredAction]}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span>
                      <strong>Seu nível:</strong>
                    </span>
                    <span className="badge bg-secondary">
                      Nível {user.superuser_level}
                    </span>
                  </div>
                </Alert>
              </div>
            </div>

            <p className="text-muted mb-3">
              <strong>💡 O que fazer:</strong> Entre em contato com seu
              administrador para solicitar acesso a este módulo.
            </p>

            {/* Debug info (temporário - remover em produção) */}
            {process.env.NODE_ENV === "development" && (
              <Alert variant="light" className="mt-4">
                <small>
                  <strong>🔍 Debug Info:</strong>
                  <br />
                  Módulo: <code>{requiredModule}</code>
                  <br />
                  Ação: <code>{requiredAction}</code>
                  <br />
                  Loading: <code>{permissionsLoading ? "Sim" : "Não"}</code>
                  <br />
                  Nível do usuário: <code>{user.superuser_level}</code>
                  <br />É superuser:{" "}
                  <code>{user.is_superuser ? "Sim" : "Não"}</code>
                </small>
              </Alert>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  }

  // ===============================================
  // 8. VERIFICAÇÃO DE PERMISSÃO CUSTOMIZADA (FUNÇÃO)
  // ===============================================
  if (requiredPermission && typeof requiredPermission === "function") {
    if (!requiredPermission(user)) {
      console.log(
        "❌ ProtectedRoute: Falhou na verificação de permissão customizada"
      );
      return (
        <div className="container mt-5">
          <Card className="border-secondary">
            <Card.Body className="text-center p-5">
              <IconShieldOff size={64} className="text-secondary mb-3" />
              <h4 className="text-secondary mb-3">Acesso Negado</h4>
              <p className="text-muted mb-4">
                Você não atende aos <strong>critérios específicos</strong>{" "}
                necessários para acessar esta área do sistema.
              </p>
              <Alert variant="secondary">
                <strong>Verificação personalizada falhada</strong>
                <br />
                <small>
                  Esta página possui regras de acesso customizadas que não foram
                  atendidas.
                </small>
              </Alert>
              <small className="text-muted">
                Entre em contato com seu administrador para mais informações.
              </small>
            </Card.Body>
          </Card>
        </div>
      );
    }
  }

  // ===============================================
  // 9. ACESSO AUTORIZADO - RENDERIZAR COMPONENTE
  // ===============================================
  console.log(
    "✅ ProtectedRoute: Todas as verificações passaram - Acesso autorizado"
  );
  console.log("🎯 ProtectedRoute: Renderizando componente protegido");

  return children;
};

export default ProtectedRoute;
